import * as Sentry from '@sentry/react'
import Cookies from 'js-cookie'
import type { ClientMethod, FetchOptions, ParseAsResponse, PathMethods } from 'openapi-fetch'
import createOpenApiClient from 'openapi-fetch'
import type {
    GetValueWithDefault,
    HttpMethod,
    MediaType,
    PathsWithMethod,
    ResponseObjectMap,
    SuccessResponse,
} from 'openapi-typescript-helpers'

import { ApiError, parseErrors } from './api-error'
import { paramsSerializer } from './params-serializer'
import type { AllPaths } from './paths'

type OpenApiData<TMethod, TAdditionalArgs> = ParseAsResponse<
    GetValueWithDefault<SuccessResponse<ResponseObjectMap<TMethod>>, MediaType, Record<string, never>>,
    TAdditionalArgs
>

type ApiHandledResponse<TMethod, TOptions> = {
    data: OpenApiData<TMethod, TOptions>
} & Pick<Response, 'headers' | 'status' | 'statusText'>

const withHandledError =
    <TMethodsByPath extends { [K in keyof TMethodsByPath]: PathMethods }, TMethodName extends HttpMethod>(
        fn: ClientMethod<TMethodsByPath, TMethodName, MediaType>,
    ) =>
        async <
            TPath extends PathsWithMethod<TMethodsByPath, TMethodName>,
            TOptions extends FetchOptions<TMethodsByPath[TPath][TMethodName]>,
        >(
            path: TPath,
            // eslint-disable-next-line @typescript-eslint/no-empty-object-type
            ...[options]: {} extends TOptions ? [TOptions?] : [TOptions]
        ): Promise<ApiHandledResponse<TMethodsByPath[TPath][TMethodName], TOptions>> => {
            try {
                const result = await fn(path, options as TOptions)

                if ('error' in result) {
                    const { message, formErrors } = parseErrors(result.error)

                    // returning here instead of throw because we are in a try/catch
                    // eslint-disable-next-line unicorn/no-useless-promise-resolve-reject
                    return Promise.reject(
                        new ApiError({
                            status: result.response.status,
                            message: message ?? result.response.statusText,
                            formErrors,
                            originalError: result.error,
                        }),
                    )
                }

                return {
                    data: result.data,
                    headers: result.response.headers,
                    status: result.response.status,
                    statusText: result.response.statusText,
                }
            } catch (error) {
                throw new ApiError({
                    status: 500,
                    message: error instanceof Error ? error.message : 'Something went wrong',
                    originalError: error instanceof Error ? error : undefined,
                })
            }
        }

const withSentryHeaders =
    <
        TMethodsByPath extends { [K in keyof TMethodsByPath]: PathMethods },
        TMethodName extends HttpMethod,
        TPath extends PathsWithMethod<TMethodsByPath, TMethodName>,
        TOptions extends FetchOptions<TMethodsByPath[TPath][TMethodName]>,
    >(
        fn: (
            path: TPath,
            // eslint-disable-next-line @typescript-eslint/no-empty-object-type
            ...[options]: {} extends TOptions ? [TOptions?] : [TOptions]
        ) => Promise<ApiHandledResponse<TMethodsByPath[TPath][TMethodName], TOptions>>,
    ) =>
        (
            path: TPath,
            // eslint-disable-next-line @typescript-eslint/no-empty-object-type
            ...[options]: {} extends TOptions ? [TOptions?] : [TOptions]
        ): Promise<ApiHandledResponse<TMethodsByPath[TPath][TMethodName], TOptions>> => {
            const currentSpan = Sentry.getActiveSpan() ?? Sentry.startInactiveSpan({ name: 'Manual Span' })
            const sentryTraceHeader = Sentry.spanToTraceHeader(currentSpan)
            const sentryBaggageHeader = Sentry.spanToBaggageHeader(currentSpan)

            const updatedOptions = {
                ...options,
                headers: {
                    ...options?.headers,
                    ...(sentryTraceHeader ? { 'sentry-trace': sentryTraceHeader } : {}),
                    ...(sentryBaggageHeader ? { baggage: sentryBaggageHeader } : {}),
                },
            }

            return fn(path, updatedOptions as TOptions)
        }

const csrftoken = Cookies.get('csrftoken')

const client = createOpenApiClient<AllPaths>({
    querySerializer: paramsSerializer,
    credentials: 'include',
    headers: csrftoken ? { 'X-CSRFToken': csrftoken } : undefined,
    baseUrl: process.env.NODE_ENV === 'test' ? 'http://localhost:9090' : undefined,
})

export const api = {
    get: withSentryHeaders(withHandledError(client.GET)),
    put: withSentryHeaders(withHandledError(client.PUT)),
    post: withSentryHeaders(withHandledError(client.POST)),
    patch: withSentryHeaders(withHandledError(client.PATCH)),
    delete: withSentryHeaders(withHandledError(client.DELETE)),
    head: withSentryHeaders(withHandledError(client.HEAD)),
}
